// src/components/practice/feedbackState.js
//
// Pure feedback mapping for the drill (lesson-3b-feedback). No DOM, no React: the
// decision "what does the student see the instant they answer" is data, so it can be
// asserted rather than eyeballed.
//
// Grading itself is NOT re-implemented here -- isQuestionCorrect stays the single
// scorer (practiceHelpers.js). This file only turns a graded answer into the words
// and the visual state that go on screen.
import { isQuestionCorrect } from './practiceHelpers.js';

// One MCQ option's visual state.
//   idle      -- not submitted, not chosen
//   selected  -- not submitted, chosen
//   correct   -- submitted, this is the right answer (whether or not it was chosen)
//   wrong     -- submitted, this is what the student chose and it is not right
//   muted     -- submitted, and this option is neither
export function optionState({ submitted, optionIndex, correctIndex, selectedIndex }) {
    const chosen = selectedIndex != null && Number(selectedIndex) === optionIndex;
    if (!submitted) return chosen ? 'selected' : 'idle';
    if (optionIndex === correctIndex) return 'correct';
    if (chosen) return 'wrong';
    return 'muted';
}

// What the feedback card says for a submitted answer, per question type.
// `detail` is the "so what is the answer then" line; null when there is nothing
// useful to add (a correct MCQ needs no explanation of itself).
export function quizFeedback(q, answer) {
    if (!q) return { correct: false, title: 'Not quite', detail: null };
    const correct = isQuestionCorrect(q, answer);

    if (q.type === 'matching') {
        const pairs = Array.isArray(q.pairs) ? q.pairs : [];
        const map = (answer && typeof answer === 'object') ? answer : {};
        const got = pairs.filter((_, i) => map[i] === i).length;
        return correct
            ? { correct: true, title: 'All pairs correct', detail: null }
            : { correct: false, title: 'Not quite', detail: got + ' of ' + pairs.length + ' pairs correct' };
    }

    if (q.type === 'fill') {
        const accepted = (Array.isArray(q.answers) ? q.answers : []).filter((a) => typeof a === 'string' && a.trim());
        return correct
            ? { correct: true, title: 'Correct', detail: null }
            : { correct: false, title: 'Not quite', detail: accepted.length ? 'Answer: ' + accepted.join(' / ') : null };
    }

    // mcq (and anything unknown, which toQuizQuestions buckets as mcq)
    const opts = Array.isArray(q.options) ? q.options : [];
    const ci = Number(q.correct);
    const letter = (Number.isInteger(ci) && ci >= 0 && ci < opts.length) ? String.fromCharCode(65 + ci) : null;
    return correct
        ? { correct: true, title: 'Correct', detail: null }
        : { correct: false, title: 'Not quite', detail: letter ? 'The answer is ' + letter + '.' : null };
}

// The compact "N to go" counter. Null when there is nothing left (the completion card
// takes over), and a singular form at the end -- "1 to go" reads worse than the nudge.
export function remainingLabel(submittedCount, total) {
    const t = Math.max(0, Math.floor(Number(total) || 0));
    const s = Math.min(t, Math.max(0, Math.floor(Number(submittedCount) || 0)));
    const left = t - s;
    if (t === 0 || left <= 0) return null;
    return left === 1 ? 'Last one' : left + ' to go';
}

// The line under a finished drill's score.
export function drillDoneMessage(score, total) {
    const t = Math.max(0, Math.floor(Number(total) || 0));
    const s = Math.min(t, Math.max(0, Math.floor(Number(score) || 0)));
    if (t === 0) return 'Drill done';
    if (s === t) return 'Nice — drill done';
    if (s * 2 >= t) return 'Good work — check the ones you missed';
    return 'Worth another pass — the explanations are above';
}

// The next CONTENT entry after the current one, skipping practice activities: the
// end-of-lesson card points at the next lesson, not at the pooled quiz. Returns null
// at the end of the lesson (the caller shows "finished this unit" instead) and for an
// id that is not in the order (never guess a position).
export function nextContentEntry(order, currentId) {
    const list = Array.isArray(order) ? order : [];
    const i = list.findIndex((e) => e && e.id === currentId);
    if (i < 0) return null;
    for (let j = i + 1; j < list.length; j++) {
        if (list[j] && list[j].kind === 'item') return list[j];
    }
    return null;
}
